import { LocalNotifications } from '@capacitor/local-notifications';
import { App } from '@capacitor/app';
import { loadAuthUser } from './auth';
import { getUserStorageKey } from '../utils/storage';

const DAILY_NOTIFICATION_ID = 1001;

export const NotificationService = {
    async hasPermission() {
        const result = await LocalNotifications.checkPermissions();
        return result.display === 'granted';
    },

    async requestPermissions() {
        const result = await LocalNotifications.requestPermissions();
        return result;
    },

    async openSettings() {
        try {
            // Use URL scheme for iOS which is more reliable for app-specific settings
            // @ts-ignore
            await App.openUrl({ url: 'app-settings:' });
        } catch (e) {
            console.warn('App.openUrl failed', e);
            // Fallback: Try direct location change (works on many iOS webviews)
            try {
                window.location.href = 'app-settings:';
            } catch (e2) {
                console.error('Failed to open settings via location', e2);
            }
        }
    },

    async scheduleDailyNotification(hour: number = 23, minute: number = 0): Promise<string> {
        const permResult = await this.requestPermissions();
        if (permResult.display !== 'granted') return permResult.display;

        // Cancel existing to avoid duplicates
        await LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIFICATION_ID }] });

        // Schedule for 11 PM every day
        const now = new Date();
        const scheduledTime = new Date();
        // setHours(hours, minutes, seconds, ms)
        // hours: 0-23 (e.g., 23 for 11 PM, 16 for 4 PM)
        // minutes: 0-59
        // seconds: 0-59
        // ms: 0-999
        // To test, manually change your device time to 10:59 PM and wait till the notification fires when it turns to 11 PM
        scheduledTime.setHours(hour, minute, 0, 0);

        if (now > scheduledTime) {
            // If it's already past the time, schedule for tomorrow
            scheduledTime.setDate(scheduledTime.getDate() + 1);
        }

        await LocalNotifications.schedule({
            notifications: [
                {
                    title: "Time to rewind",
                    body: "It's bed time, chat with Lumi about your day to rewind!",
                    id: DAILY_NOTIFICATION_ID,
                    schedule: {
                        at: scheduledTime,
                        every: 'day',
                        allowWhileIdle: true,
                    },
                    sound: 'default',
                    attachments: [],
                    actionTypeId: '',
                    extra: null,
                },
            ],
        });
        return 'granted';
    },

    async cancelDailyNotification() {
        await LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIFICATION_ID }] });
    },

    async initialize() {
        const permStatus = await LocalNotifications.checkPermissions();
        if (permStatus.display === 'prompt' || permStatus.display === 'granted') {
            // Try to get saved settings
            let hour = 23;
            let minute = 0;
            try {
                const user = loadAuthUser();
                const savedSettings = localStorage.getItem(getUserStorageKey('app-settings', user?.id));
                if (savedSettings) {
                    const parsed = JSON.parse(savedSettings);
                    if (parsed.notificationTime) {
                        const [h, m] = parsed.notificationTime.split(':').map(Number);
                        hour = h;
                        minute = m;
                    }
                }
            } catch (e) {
                console.error('Failed to parse settings for notification time', e);
            }

            // If prompt (default) or granted, try to schedule (which requests permission)
            this.scheduleDailyNotification(hour, minute);
        }
    }
};
